import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import OverlayModal from '../ui/OverlayModal';
import {
  isAttorneyGeneralCoverNote,
  isCivilAppellateCoverNote,
  isCivilAdviseCoverNote,
  isNominationCoverNote,
  type CommonCoverNoteResponse,
} from '../../types/coverNoteApi';
import * as coverNoteService from '../../services/coverNoteRequests';
import { setFlashToast } from '../../utils/flashToast';
import { formatDisplayDate, formatDisplayDateTime } from '../../utils/dateFormat';
import { downloadCoverNotePdfFile, printCoverNotePdf } from '../../utils/coverNotePrint';
import { coverNoteQrImageSrc } from '../../utils/coverNoteQr';
import { criminalCoverNoteDivisionUnit } from '../../utils/criminalCoverNoteDisplay';
import { wizardFlowForCategoryName } from '../../utils/wizardCategoryRoute';

type CommonCoverNoteModalProps = {
  isOpen: boolean;
  caseRequestId: string;
  fileRefFallback?: string;
  coverNoteFallback?: Partial<CommonCoverNoteResponse>;
  showSuccessBanner?: boolean;
  showCaseSource?: boolean;
  onClose: () => void;
};

function displayValue(value: string | null | undefined): string {
  const t = value?.trim();
  return t || '—';
}

function displayList(value: string[] | null | undefined): string {
  if (!value) return '—';
  const items = value.map((s) => s.trim()).filter(Boolean);
  return items.length ? items.join(', ') : '—';
}

function CoverNoteSummaryField({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="csf">
      <label>{label}</label>
      <p>{displayValue(value)}</p>
    </div>
  );
}

function declaredDocuments(documents: string[] | undefined): string[] {
  return (documents ?? []).map((d) => d.trim()).filter(Boolean);
}

function CoverNoteDocumentsDeclared({ documents, label }: { documents: string[]; label: string }) {
  return (
    <div className="csf">
      <label>{label}</label>
      {documents.length ? (
        <div className="dtags">
          {documents.map((doc, index) => (
            <div key={`${doc}-${index}`} className="dtag">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
                <polyline points="20 6 9 17 4 12" />
              </svg>
              {doc}
            </div>
          ))}
        </div>
      ) : (
        <p className="ccr-review-val">—</p>
      )}
    </div>
  );
}

function CoverNoteSummaryGrid({
  coverNote,
  showCaseSource = true,
}: {
  coverNote: CommonCoverNoteResponse;
  showCaseSource?: boolean;
}) {
  const { t } = useTranslation();
  const documents = declaredDocuments(coverNote.documents);
  const isCivilAdvise = isCivilAdviseCoverNote(coverNote);
  const showCaseSourceField =
    showCaseSource &&
    !isNominationCoverNote(coverNote) &&
    !isCivilAdvise &&
    !isAttorneyGeneralCoverNote(coverNote);
  const showDocumentsDeclared = !isNominationCoverNote(coverNote);

  if (isCivilAppellateCoverNote(coverNote) || isCivilAdvise) {
    return (
      <div className="cn-sum-grid">
        <div className="cn-col">
          <CoverNoteSummaryField label={t('caseRequests.connectedRef')} value={coverNote.connectedAgdFileReferenceNumber} />
          <div className="csf">
            <label>{t('caseRequests.applicableAreas')}</label>
            <p>{displayList(coverNote.applicableAreas)}</p>
          </div>
          <CoverNoteSummaryField label={t('wizard.fields.description')} value={coverNote.description} />
        </div>
        <div className="cn-col">
          <CoverNoteSummaryField label={t('caseRequests.urgencyLevel')} value={coverNote.urgencyLevel} />
          <CoverNoteSummaryField label={t('caseRequests.deadline')} value={formatDisplayDate(coverNote.deadline) || null} />
          <CoverNoteSummaryField label={t('caseRequests.urgencyJustification')} value={coverNote.urgencyJustification} />
          {showDocumentsDeclared ? (
            <CoverNoteDocumentsDeclared documents={documents} label={t('caseRequests.documentsDeclared')} />
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="cn-sum-grid">
      <div className="cn-col">
        {showCaseSourceField ? <CoverNoteSummaryField label={t('caseRequests.caseSource')} value={coverNote.caseSource} /> : null}
        <CoverNoteSummaryField label={t('caseRequests.connectedRef')} value={coverNote.connectedAgdFileReferenceNumber} />
        <div className="csf">
          <label>{t('caseRequests.applicableAreas')}</label>
          <p>{displayList(coverNote.applicableAreas)}</p>
        </div>
        <CoverNoteSummaryField label={t('wizard.fields.description')} value={coverNote.description} />
      </div>
      <div className="cn-col">
        <CoverNoteSummaryField label={t('caseRequests.urgencyLevel')} value={coverNote.urgencyLevel} />
        <CoverNoteSummaryField label={t('caseRequests.deadline')} value={formatDisplayDate(coverNote.deadline) || null} />
        <CoverNoteSummaryField label={t('caseRequests.urgencyJustification')} value={coverNote.urgencyJustification} />
        {showDocumentsDeclared ? (
          <CoverNoteDocumentsDeclared documents={documents} label={t('caseRequests.documentsDeclared')} />
        ) : null}
      </div>
    </div>
  );
}

function CoverNoteBody({
  coverNote,
  qrSrc,
  showCaseSource = true,
}: {
  coverNote: CommonCoverNoteResponse;
  qrSrc: string;
  showCaseSource?: boolean;
}) {
  const { t } = useTranslation();

  return (
    <div className="cn-body">
      <div className="cn-meta cn-meta-qf">
        <div className="cn-qr-box">
          {qrSrc ? (
            <img src={qrSrc} alt={t('viewRequest.qrAlt')} width={68} height={68} style={{ display: 'block' }} />
          ) : (
            <div style={{ width: 68, height: 68, background: 'var(--bg)' }} aria-hidden />
          )}
          <div className="cn-qr-note">{t('caseRequests.qrNote')}</div>
        </div>
        <div className="cn-box">
          <label>{t('caseRequests.fileReferenceNumber')}</label>
          <p>{displayValue(coverNote.fileReferenceNumber)}</p>
        </div>
        <div className="cn-box">
          <label>{t('caseRequests.requestSubmittedAt')}</label>
          <p>{formatDisplayDateTime(coverNote.requestSubmittedAt) || '—'}</p>
        </div>
        <div className="cn-box">
          <label>{t('caseRequests.divisionUnit')}</label>
          <p>
            {displayValue(
              criminalCoverNoteDivisionUnit(coverNote.submittedDivisionName, coverNote.submittedAgency),
            )}
          </p>
        </div>
        <div className="cn-box">
          <label>{t('tasks.shared.fileCategory')}</label>
          <p className="cat-val">{displayValue(coverNote.fileCategory)}</p>
          <p className="sub">{t('caseRequests.subFileCategoryLabel', { value: displayValue(coverNote.subFileCategory) })}</p>
        </div>
      </div>

      <div className="cn-sum-title">{t('caseRequests.summaryTitle')}</div>
      <CoverNoteSummaryGrid coverNote={coverNote} showCaseSource={showCaseSource} />
    </div>
  );
}

type CommonCoverNoteModalContentProps = {
  caseRequestId: string;
  fileRefFallback?: string;
  coverNoteFallback?: Partial<CommonCoverNoteResponse>;
  showSuccessBanner?: boolean;
  showCaseSource?: boolean;
  onClose: () => void;
};

function CommonCoverNoteModalContent({
  caseRequestId,
  fileRefFallback,
  coverNoteFallback,
  showSuccessBanner = false,
  showCaseSource = true,
  onClose,
}: CommonCoverNoteModalContentProps) {
  const { t } = useTranslation();
  const id = caseRequestId.trim();
  const [apiCoverNote, setApiCoverNote] = useState<CommonCoverNoteResponse | null>(null);
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    if (!id) {
      setFlashToast({ message: t('viewRequest.caseRequestIdMissing'), variant: 'error' });
      return;
    }

    let mounted = true;
    void coverNoteService.getCommonCoverNote(id).then((r) => {
      if (!mounted) return;
      setLoadedKey(id);
      if (!r.success) {
        setFlashToast({ message: r.message ?? t('caseRequests.coverNoteLoadFailed'), variant: 'error' });
        return;
      }
      setApiCoverNote(r.data);
    });

    return () => {
      mounted = false;
    };
  }, [id, t]);

  const coverNote = useMemo(
    () =>
      apiCoverNote
        ? coverNoteService.mergeCommonCoverNoteResponse(apiCoverNote, coverNoteFallback)
        : null,
    [apiCoverNote, coverNoteFallback],
  );

  const loading = Boolean(id) && loadedKey !== id;
  const fileRef = coverNote?.fileReferenceNumber?.trim() || fileRefFallback?.trim() || '—';

  const downloadPdf = async () => {
    if (downloading || !coverNote || !id) return;

    setDownloading(true);
    try {
      const flow = wizardFlowForCategoryName(coverNote.fileCategory);
      const r = await downloadCoverNotePdfFile(id, flow, fileRef);
      if (!r.success) {
        setFlashToast({ message: r.message ?? t('viewRequest.coverNotePdfFailed'), variant: 'error' });
      }
    } finally {
      setDownloading(false);
    }
  };

  const printCoverNote = async () => {
    if (!coverNote || printing || !id) return;

    setPrinting(true);
    try {
      const flow = wizardFlowForCategoryName(coverNote.fileCategory);
      const r = await printCoverNotePdf(id, flow, fileRef);
      if (!r.success) {
        setFlashToast({ message: r.message ?? t('viewRequest.coverNotePdfFailed'), variant: 'error' });
      }
    } finally {
      setPrinting(false);
    }
  };

  if (!id) {
    return (
      <div className="cn-scroll">
        <div className="cn-hdr">{t('caseRequests.coverNote')}</div>
        <div className="cn-body" style={{ textAlign: 'center', color: 'var(--t3)', padding: '40px 20px' }}>
          {t('caseRequests.coverNoteUnavailable')}
          <div style={{ marginTop: 16 }}>
            <button className="mfb mfb-dk" type="button" onClick={onClose}>
              {t('common.close')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const qrSrc = coverNote ? coverNoteQrImageSrc(coverNote.qr) : '';

  if (loading) {
    return (
      <div className="cn-scroll">
        <div className="cn-hdr">{t('caseRequests.coverNote')}</div>
        <div className="cn-body" style={{ textAlign: 'center', color: 'var(--t3)', padding: '40px 20px' }}>
          {t('caseRequests.loadingCoverNote')}
        </div>
      </div>
    );
  }

  if (!coverNote) {
    return (
      <div className="cn-scroll">
        <div className="cn-hdr">{t('caseRequests.coverNote')}</div>
        <div className="cn-body" style={{ textAlign: 'center', color: 'var(--t3)', padding: '40px 20px' }}>
          {t('caseRequests.coverNoteUnavailable')}
          <div style={{ marginTop: 16 }}>
            <button className="mfb mfb-dk" type="button" onClick={onClose}>
              {t('common.close')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {showSuccessBanner ? (
        <div className="success-bar cn-cover-success">
          <div className="success-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <div>
            <div className="success-title">Submission successful</div>
            <div className="success-sub">
              Request reference <strong>{fileRef}</strong> has been created and timestamped. Make sure to attach the printed Cover
              Note before handing over the physical case file to AGD.
            </div>
          </div>
        </div>
      ) : null}

      <div className="cn-printable cn-scroll">
        <div className="cn-hdr">{t('caseRequests.coverNote')}</div>
        <CoverNoteBody coverNote={coverNote} qrSrc={qrSrc} showCaseSource={showCaseSource} />
      </div>
      <div className="cn-footer">
        <button className="mfb" type="button" disabled={downloading} onClick={() => void downloadPdf()}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          {downloading ? t('caseRequests.downloading') : t('caseRequests.downloadPdf')}
        </button>
        <button className="mfb" type="button" disabled={printing} onClick={() => void printCoverNote()}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <polyline points="6 9 6 2 18 2 18 9" />
            <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
            <rect x="6" y="14" width="12" height="8" />
          </svg>
          {printing ? t('viewRequest.preparing') : t('caseRequests.print')}
        </button>
        <button className="mfb mfb-dk" type="button" onClick={onClose}>
          {t('common.close')}
        </button>
      </div>
    </>
  );
}

export default function CommonCoverNoteModal({
  isOpen,
  caseRequestId,
  fileRefFallback,
  coverNoteFallback,
  showSuccessBanner = false,
  showCaseSource = true,
  onClose,
}: CommonCoverNoteModalProps) {
  return (
    <OverlayModal
      isOpen={isOpen}
      overlayClassName="overlay"
      contentClassName="cn-mbox cn-mbox--cover"
      centerVertically
      onClose={onClose}
    >
      {isOpen ? (
        <CommonCoverNoteModalContent
          key={caseRequestId}
          caseRequestId={caseRequestId}
          fileRefFallback={fileRefFallback}
          coverNoteFallback={coverNoteFallback}
          showSuccessBanner={showSuccessBanner}
          showCaseSource={showCaseSource}
          onClose={onClose}
        />
      ) : null}
    </OverlayModal>
  );
}
